import jsPDF from "jspdf";
import { loadLogoForPdf } from "../convocations/logoLoader";

/**
 * Convert an inline drill diagram (pieces+drawings) into a PNG data URL via offscreen canvas.
 * Uses the same coordinate system as DrillBoardDialog (100x100 viewbox).
 */
async function drillDiagramToPng(diagram, size = 280) {
  if (!diagram || !diagram.pieces?.length) return null;
  const W = size;
  const H = Math.round(size * 0.75);
  const xml = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 75" width="${W}" height="${H}">
    <rect width="100" height="75" fill="#15803d" rx="3" />
    <g stroke="rgba(255,255,255,0.5)" stroke-width="0.2" fill="none">
      <rect x="2" y="2" width="96" height="71" />
      <line x1="50" y1="2" x2="50" y2="73" />
      <circle cx="50" cy="37.5" r="6" />
    </g>
    ${(diagram.drawings || []).map((d) => {
      if (d.type === "line" || d.type === "arrow") {
        const [a, b] = d.points || [];
        if (!a || !b) return "";
        return `<line x1="${a.x}" y1="${a.y * 0.75}" x2="${b.x}" y2="${b.y * 0.75}" stroke="${d.color || "#fff"}" stroke-width="0.4" />`;
      }
      return "";
    }).join("")}
    ${(diagram.pieces || []).map((p) => {
      const x = p.x;
      const y = p.y * 0.75;
      if (p.type === "cone") return `<polygon points="${x},${y - 2.5} ${x + 2},${y + 1.5} ${x - 2},${y + 1.5}" fill="#fb923c" />`;
      if (p.type === "ball") return `<circle cx="${x}" cy="${y}" r="1.5" fill="white" stroke="#0f172a" stroke-width="0.2" />`;
      if (p.type === "hurdle") return `<rect x="${x - 2.5}" y="${y - 0.7}" width="5" height="1.4" fill="#facc15" />`;
      // default: player
      return `<g><circle cx="${x}" cy="${y}" r="2.5" fill="${p.color || "#0ea5e9"}" stroke="white" stroke-width="0.3" /><text x="${x}" y="${y + 0.8}" font-size="2" text-anchor="middle" fill="white" font-weight="700">${p.label || ""}</text></g>`;
    }).join("")}
  </svg>`;
  const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, W, H);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function fetchImageAsDataUrl(url, maxDim = 260) {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const img = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
    const W = Math.round(img.width * ratio);
    const H = Math.round(img.height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, W, H);
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch {
    return null;
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return [14, 165, 233];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

/**
 * Build a polished training PDF: logo + team color band + structured drills + diagrams + reference photos + footer.
 */
export async function exportTrainingPdf(training, team) {
  const doc = new jsPDF();
  const pageW = 210;
  const pageH = 297;
  const margin = 18;
  const [r, g, b] = hexToRgb(team?.color || "#0ea5e9");

  // ----- Header banner with team color
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageW, 32, "F");
  // Logo
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try {
      doc.addImage(logo.dataUrl, logo.format, 12, 7, 18, 18);
    } catch (e) { console.warn("PDF logo add failed:", e); }
  }
  // Header text white
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(18);
  doc.text("Seduta Allenamento", logo ? 36 : 18, 14);
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  const teamLine = `${team?.name || ""} · ${team?.category || ""}${team?.season ? ` · Stagione ${team.season}` : ""}`;
  const teamLineMaxX = pageW - margin - 40; // reserve 40mm for the date on the right
  const teamLineX = logo ? 36 : 18;
  const teamLineWidth = teamLineMaxX - teamLineX;
  const teamLineClipped = doc.splitTextToSize(teamLine, teamLineWidth)[0] || teamLine;
  doc.text(teamLineClipped, teamLineX, 22);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }), pageW - margin, 22, { align: "right" });

  doc.setTextColor(15, 23, 42);
  let y = 44;

  // ----- Title + metadata block
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text(training.title || "—", margin, y);
  y += 7;
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 116, 139);
  const meta = [
    training.date,
    `${training.duration_min || 90} min`,
    training.location,
  ].filter(Boolean).join(" · ");
  doc.text(meta, margin, y);
  doc.setTextColor(15, 23, 42);
  y += 8;

  // ----- Objectives
  if (training.objectives) {
    doc.setFillColor(241, 245, 249);
    const lines = doc.splitTextToSize(training.objectives, pageW - margin * 2 - 6);
    const boxH = 9 + lines.length * 4.6;
    doc.roundedRect(margin, y, pageW - margin * 2, boxH, 2, 2, "F");
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("Obiettivi", margin + 3, y + 6);
    doc.setFont(undefined, "normal");
    doc.text(lines, margin + 3, y + 11);
    y += boxH + 5;
  }

  // ----- Drills loop
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(r, g, b);
  doc.text("ESERCIZI", margin, y);
  doc.setTextColor(15, 23, 42);
  y += 6;

  const drills = training.drills || [];
  for (let i = 0; i < drills.length; i++) {
    const d = drills[i];

    // Pre-compute the photos / diagrams we want to embed for height estimate
    const diagramPng = await drillDiagramToPng(d.diagram, 320).catch(() => null);
    const photoPng = d.image_url ? await fetchImageAsDataUrl(d.image_url, 260) : null;
    const visualH = diagramPng || photoPng ? 45 : 0;
    const descLines = d.description ? doc.splitTextToSize(d.description, pageW - margin * 2 - 6) : [];
    const cardH = 18 + descLines.length * 4.6 + visualH;

    if (y + cardH > pageH - 20) {
      doc.addPage();
      y = margin;
    }

    // Card background
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(margin, y, pageW - margin * 2, cardH, 2.5, 2.5, "FD");

    // Number badge
    doc.setFillColor(r, g, b);
    doc.circle(margin + 6, y + 7, 4, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text(String(i + 1), margin + 6, y + 8.5, { align: "center" });
    doc.setTextColor(15, 23, 42);

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text(d.name || `Esercizio ${i + 1}`, margin + 13, y + 8);
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 116, 139);
    const drillMeta = [d.category, d.duration ? `${d.duration} min` : null].filter(Boolean).join(" · ");
    if (drillMeta) doc.text(drillMeta, pageW - margin - 4, y + 8, { align: "right" });
    doc.setTextColor(15, 23, 42);

    let yy = y + 15;
    if (descLines.length) {
      doc.setFontSize(9);
      doc.text(descLines, margin + 5, yy);
      yy += descLines.length * 4.6;
    }
    if (diagramPng) {
      try {
        doc.addImage(diagramPng, "PNG", margin + 5, yy + 1, 60, 45);
      } catch (e) { console.warn("PDF drill diagram add failed:", e); }
    }
    if (photoPng) {
      try {
        doc.addImage(photoPng, "JPEG", margin + (diagramPng ? 70 : 5), yy + 1, 60, 45);
      } catch (e) { console.warn("PDF drill photo add failed:", e); }
    }

    y += cardH + 4;
  }

  // ----- Attachments list (training-level)
  const attachments = training.attachments || [];
  if (attachments.length) {
    if (y > pageH - 30) { doc.addPage(); y = margin; }
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.setTextColor(r, g, b);
    doc.text("ALLEGATI", margin, y);
    doc.setTextColor(15, 23, 42);
    y += 6;
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    attachments.forEach((a) => {
      doc.text(`• ${a.filename} (${Math.round((a.size || 0) / 1024)} KB)`, margin + 4, y);
      y += 5;
    });
  }

  // ----- Footer
  const pages = doc.internal.pages.length - 1;
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, pageH - 14, pageW - margin, pageH - 14);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text(`${team?.name || ""} · Mister ____________________`, margin, pageH - 9);
    doc.text(`Pagina ${p} di ${pages}`, pageW - margin, pageH - 9, { align: "right" });
  }

  doc.save(`allenamento-${(training.title || "seduta").replace(/\s+/g, "-")}.pdf`);
}
